import React, { useEffect, useMemo, useState } from "react";
import { Modal, ModalHeader, ModalBody, Row, Col, Label, Button, Input, FormFeedback } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";

import { selectCustomStyles } from "../../../common/selectCustomStyles";

const CourseUnitModal = ({
  modal,
  toggle,
  isEdit,
  courseUnit,
  semesters = [],
  courseComponents = [],
  teachers = [],
  onSubmit
}) => {

  const [filteredCourseComponents, setFilteredCourseComponents] = useState([]);

  // 🔹 Formik
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: courseUnit?.id || "",
      uuid: courseUnit?.uuid || '',
      title: courseUnit?.title || '',
      code: courseUnit?.code || '',
      semester_id: courseUnit?.semester_id || '',
      responsible_teacher_id: courseUnit?.responsible_teacher_id || '',
      credit: courseUnit?.credit || '',
      course_unit_course_component_ids: courseUnit?.course_components
        ? courseUnit.course_components.map(s => s.id)
        : []
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Veuillez saisir le titre"),
      semester_id: Yup.number().required("Veuillez sélectionner le semestre"),
      responsible_teacher_id: Yup.number().required("Veuillez sélectionner l'enseignant responsable"),
      course_unit_course_component_ids: Yup.array()
        .min(1, "Sélectionnez au moins un élément constitutif")
        .required("Champ obligatoire")
    }),
    onSubmit: values => {
      const payload = {
        ...values,
        credit: Number(values.credit),
        semester_id: Number(values.semester_id),
        responsible_teacher_id: Number(values.responsible_teacher_id),
        course_unit_course_component_ids: values.course_unit_course_component_ids.map(Number)
      };
      onSubmit(payload);
    }
  });

  // 🔹 Filtrer les éléments constitutifs selon le semestre sélectionné
  useEffect(() => {
    if (formik.values.semester_id) {
      const filtered = courseComponents.filter(
        cc => cc.semester_id === parseInt(formik.values.semester_id)
      );
      setFilteredCourseComponents(filtered);

      // Supprimer les IDs sélectionnés qui ne sont plus valides
      const validIds = filtered.map(cc => cc.id);
      const newSelected = formik.values.course_unit_course_component_ids.filter(id => validIds.includes(id));
      if (newSelected.length !== formik.values.course_unit_course_component_ids.length) {
        formik.setFieldValue("course_unit_course_component_ids", newSelected);
      }
    } else {
      setFilteredCourseComponents([]);
      formik.setFieldValue("course_unit_course_component_ids", []);
    }
  }, [formik.values.semester_id, courseComponents]);

  // 🔹 Options pour react-select
  const courseComponentOptions = useMemo(() => 
    filteredCourseComponents.map(cc => ({ value: cc.id, label: cc.title })), 
    [filteredCourseComponents]
  );

  return (
    <Modal isOpen={modal} toggle={toggle} backdrop="static">
      <ModalHeader toggle={toggle}>
        {isEdit ? "Modifier - Unité d'enseignement" : "Ajouter - Unité d'enseignement"}
      </ModalHeader>
      <ModalBody>
        <form onSubmit={formik.handleSubmit}>
          <Row>
            <Col className="col-12">

              <div className="mb-3">
                <Label>Titre</Label>
                <Input
                  name="title"
                  type="text"
                  placeholder="Titre"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.title || ""}
                  invalid={formik.touched.title && formik.errors.title}
                />
                {formik.touched.title && formik.errors.title && (
                  <FormFeedback type="invalid">{formik.errors.title}</FormFeedback>
                )}
              </div>

              <div className="mb-3">
                <Label>Code</Label>
                <Input
                  name="code"
                  type="text"
                  placeholder="Code"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.code || ""}
                  invalid={formik.touched.code && formik.errors.code}
                />
                {formik.touched.code && formik.errors.code && (
                  <FormFeedback type="invalid">{formik.errors.code}</FormFeedback>
                )}
              </div>

              <div className="mb-3">
                <Label>Semestre</Label>
                <Input
                  name="semester_id"
                  type="select"
                  className="form-select"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.semester_id || ""}
                  invalid={formik.touched.semester_id && formik.errors.semester_id}
                >
                  <option value="">-- Sélectionner --</option>
                  {semesters.map(s => (
                    <option key={s.id} value={s.id}>{s.code}</option>
                  ))}
                </Input>
                {formik.touched.semester_id && formik.errors.semester_id && (
                  <FormFeedback type="invalid">{formik.errors.semester_id}</FormFeedback>
                )}
              </div>

              <div className="mb-3">
                <Label>Éléments constitutifs</Label>
                <Select
                  name="course_unit_course_component_ids"
                  isSearchable
                  isClearable
                  menuPortalTarget={null}
                  styles={selectCustomStyles}
                  options={courseComponentOptions}
                  isMulti
                  value={courseComponentOptions.filter(option =>
                    formik.values.course_unit_course_component_ids.includes(option.value)
                  )}
                  onChange={selectedOptions => {
                    const values = selectedOptions ? selectedOptions.map(o => o.value) : [];
                    formik.setFieldValue("course_unit_course_component_ids", values);
                  }}
                  onBlur={() => formik.setFieldTouched("course_unit_course_component_ids", true)}
                />
                {formik.touched.course_unit_course_component_ids && formik.errors.course_unit_course_component_ids && (
                  <div className="text-danger mt-1">{formik.errors.course_unit_course_component_ids}</div>
                )}
              </div>

              <div className="mb-3">
                <Label>Enseignant responsable</Label>
                <Input
                  name="responsible_teacher_id"
                  type="select"
                  className="form-select"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.responsible_teacher_id || ""}
                  invalid={formik.touched.responsible_teacher_id && formik.errors.responsible_teacher_id}
                >
                  <option value="">-- Sélectionner --</option>
                  {teachers.map(t => (
                    <option key={t.id} value={t.id}>{`${t.first_name} ${t.last_name}`}</option>
                  ))}
                </Input>
                {formik.touched.responsible_teacher_id && formik.errors.responsible_teacher_id && (
                  <FormFeedback type="invalid">{formik.errors.responsible_teacher_id}</FormFeedback>
                )}
              </div>

              <div className="mb-3">
                <Label>Crédit</Label>
                <Input
                  name="credit"
                  type="number"
                  placeholder="Crédit"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.credit || ""}
                  invalid={formik.touched.credit && formik.errors.credit}
                />
                {formik.touched.credit && formik.errors.credit && (
                  <FormFeedback type="invalid">{formik.errors.credit}</FormFeedback>
                )}
              </div>

            </Col>
          </Row>

          <Row>
            <Col>
              <div className="text-end">
                <Button type="submit" color="primary">{isEdit ? "Modifier" : "Enregistrer"}</Button>
              </div>
            </Col>
          </Row>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default CourseUnitModal;